import { AlertTriangle, Briefcase, Car, Home } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Input } from '@/components/Shared/Input';
import { ChoiceTiles } from '@/components/Shared/ChoiceTiles';
import type { FacilityFlowCoreModulesFleetEntitiesTripPurposeKind as PurposeKind } from '@/api/generated/models';
import type { VehicleDto } from '@/store/useVehicleStore';
import { formatKm } from '@/utils/distance';
import { formatUtcToLocalTime } from '@/utils/dateUtils';
import { DESTINATION_MAX_LENGTH, PURPOSE_MAX_LENGTH } from '@/utils/logbookLimits';
import { BUSINESS, PRIVATE, parseKm } from '@/utils/vehicleTrips';

export interface TripDriverValues {
  odometer: string;
  startLocation: string;
  destination: string;
  purpose: string;
  purposeKind: PurposeKind;
}

interface TripDriverFieldsProps {
  vehicle: VehicleDto | undefined;
  values: TripDriverValues;
  allowPrivateTrips: boolean;
  onChange: (change: Partial<TripDriverValues>) => void;
}

const FIELD = 'h-11 text-[15px]';

/**
 * What the driver enters when a trip starts (official logbook, FAFD-68): the start reading,
 * prefilled with the vehicle's last one, and the start location, prefilled with its last
 * destination. A different reading only warns — the logbook marks the gap. Destination and
 * purpose come prefilled from the duty plan (or "laut Dienstplan") and can be changed; they are
 * optional here and asked again when the trip ends; business or private only where the
 * tenant allows private trips.
 */
export function TripDriverFields({ vehicle, values, allowPrivateTrips, onChange }: TripDriverFieldsProps) {
  const { t } = useTranslation('vehicles');
  const km = parseKm(values.odometer);
  const lastKm = vehicle?.currentOdometerKm ?? null;
  const openTrip = vehicle?.openTrip;
  const isPrivate = values.purposeKind === PRIVATE;

  return (
    <div className="space-y-4">
      {openTrip && (
        <p className="flex gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
          <Car className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" strokeWidth={2} aria-hidden />
          <span>
            {/* Employees do not get the driver's name — only the administration does. */}
            {t(openTrip.driverName ? 'trip.takeover' : 'trip.takeoverNoDriver', {
              time: formatUtcToLocalTime(openTrip.startedAt),
              driver: openTrip.driverName,
              km: formatKm(openTrip.startOdometerKm),
            })}
          </span>
        </p>
      )}

      {allowPrivateTrips && (
        <ChoiceTiles
          legend={t('trip.kindLabel')}
          compact
          value={isPrivate ? 'private' : 'business'}
          onChange={(kind) => onChange({ purposeKind: kind === 'private' ? PRIVATE : BUSINESS })}
          options={[
            { value: 'business', label: t('trip.kind.business'), icon: Briefcase },
            { value: 'private', label: t('trip.kind.private'), icon: Home },
          ]}
        />
      )}

      <div>
        <Input
          label={`${t('clock.odometerLabel')} *`}
          inputMode="numeric"
          autoComplete="off"
          value={values.odometer}
          onChange={(e) => onChange({ odometer: e.target.value.replace(/\D/g, '') })}
          placeholder={t('clock.odometerPlaceholder')}
          className={`${FIELD} font-mono tabular-nums`}
        />
        <span className="mt-1 block text-[12px] text-slate-500 tabular-nums">
          {lastKm != null ? t('clock.lastOdometer', { km: formatKm(lastKm) }) : t('clock.noOdometer')}
        </span>
      </div>

      {km !== null && lastKm != null && km !== lastKm && (
        <p className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" strokeWidth={2} aria-hidden />
          <span>
            {km < lastKm
              ? t('clock.odometerBelowLast', { km: formatKm(lastKm) })
              : t('trip.odometerAboveLast', { km: formatKm(lastKm), gap: formatKm(km - lastKm) })}
          </span>
        </p>
      )}

      <Input
        label={t('trip.startLocationLabel')}
        value={values.startLocation}
        maxLength={DESTINATION_MAX_LENGTH}
        onChange={(e) => onChange({ startLocation: e.target.value })}
        placeholder={t('trip.startLocationPlaceholder')}
        className={FIELD}
      />
      <Input
        label={t('clock.destinationLabel')}
        value={values.destination}
        maxLength={DESTINATION_MAX_LENGTH}
        onChange={(e) => onChange({ destination: e.target.value })}
        placeholder={t('clock.destinationPlaceholder')}
        className={FIELD}
      />
      {!isPrivate && (
        <Input
          label={t('clock.purposeLabel')}
          value={values.purpose}
          maxLength={PURPOSE_MAX_LENGTH}
          onChange={(e) => onChange({ purpose: e.target.value })}
          placeholder={t('clock.purposePlaceholder')}
          className={FIELD}
        />
      )}
    </div>
  );
}
